import { ENVIRONMENT_AUTH_DETAILS } from '@motional-cc/fe/tools/auth';
import {
  AuthApiError,
  OktaAuth,
  OktaAuthOptions,
  SignoutOptions,
} from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { set as idbSet } from 'idb-keyval';
import { useCallback } from 'react';
import { env } from 'src/api/hosts';
import { VITE_ACCESS_TOKEN_OVERRIDE } from 'src/config/env';

const authOptions =
  (env && ENVIRONMENT_AUTH_DETAILS[env]) || ENVIRONMENT_AUTH_DETAILS.dev;
const OKTA_CONFIG: OktaAuthOptions = {
  clientId: authOptions.clientId,
  issuer: `${authOptions.issuerPath}${authOptions.issuerId}`,
  scopes: ['openid', 'email', 'profile', 'offline_access'],
  redirectUri: `${globalThis.location.origin}/callback`,
  // Get the username/password from localStore when running cypress tests
  tokenManager:
    globalThis.Cypress ?
      {
        storage: 'localStorage',
        storageKey: 'oktaCypress',
      }
    : {
        autoRenew: true,
      },
};

const SIGNOUT_OPTIONS: SignoutOptions = {
  revokeAccessToken: true,
  revokeRefreshToken: true,
  postLogoutRedirectUri: globalThis.location.origin,
};

export const oktaAuth = new OktaAuth(OKTA_CONFIG);

oktaAuth.tokenManager.on('renewed', async (key, token) => {
  try {
    await idbSet(key, token);
  } catch (_error) {
    alert(
      'Notifications can’t be saved. Refresh the page to ensure notifications keep working.',
    );
  }
});

oktaAuth.tokenManager.on('added', async (key, token) => {
  try {
    await idbSet(key, token);
  } catch (_error) {
    alert(
      'Notifications can’t be saved. Refresh the page to ensure notifications keep working.',
    );
  }
});

export const oktaLogout = async () => {
  try {
    await oktaAuth?.signOut(SIGNOUT_OPTIONS);
  } catch (_error) {
    const error = _error as AuthApiError;
    alert(
      `Attempt to log out failed with the following error:\n${error.name} — ${
        error.message ??
        (error.xhr as Error | undefined)?.message ??
        'something went wrong'
      }\n\nGive it a minute. You may already be logged out. If not, contact support.`,
    );
  }
};

export const getAccessToken = (
  oktaAuth: Pick<OktaAuth, 'getAccessToken'>,
  accessTokenOverride = VITE_ACCESS_TOKEN_OVERRIDE,
) => accessTokenOverride || oktaAuth.getAccessToken();

export const useGetAccessToken = () => {
  const { oktaAuth } = useOktaAuth();
  return useCallback(() => getAccessToken(oktaAuth), [oktaAuth]);
};

export const useIsAuthenticated = () => {
  const { authState } = useOktaAuth();
  if (VITE_ACCESS_TOKEN_OVERRIDE) {
    return !!VITE_ACCESS_TOKEN_OVERRIDE;
  }
  return authState?.isAuthenticated;
};
